import { useMemo, useState } from "react"
import { Badge } from "@ui/badge"
import { Button } from "@ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/select"
import { Skeleton } from "@ui/skeleton"
import { Ban, Loader2, MessageSquare, Phone, User } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"

import {
  useContactConversations,
  useConversationTimeline,
} from "../hooks/use-conversation-timeline"
import { AdminPageContainer } from "./admin-page-container"
import { TimelineEventCard } from "./timeline-event-card"

export function ConversationTimeline() {
  const { contactId } = useParams<{ contactId: string }>()
  const navigate = useNavigate()
  const { data: contactData, isLoading: loadingContact } = useContactConversations(contactId)

  const [selectedConversationId, setSelectedConversationId] = useState<string>("")

  const activeConversationId = selectedConversationId || contactData?.conversations[0]?.id

  const {
    data: timelineData,
    isLoading: loadingTimeline,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useConversationTimeline(activeConversationId)

  const allEvents = useMemo(
    () => timelineData?.pages.flatMap(page => page.events) ?? [],
    [timelineData]
  )

  if (loadingContact) {
    return (
      <AdminPageContainer title="Timeline" showBackButton backTo="/admin/conversations">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-12 w-full" />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </AdminPageContainer>
    )
  }

  if (!contactData) {
    return (
      <AdminPageContainer title="Timeline" showBackButton backTo="/admin/conversations">
        <div className="text-muted-foreground py-12 text-center">Contato não encontrado</div>
      </AdminPageContainer>
    )
  }

  const { contact, conversations } = contactData

  return (
    <AdminPageContainer
      title="Timeline da Conversa"
      subtitle={contact.phone_number}
      showBackButton
      backTo="/admin/conversations"
    >
      {/* Contact info card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Contato</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <User className="text-muted-foreground h-4 w-4" />
              <span className="text-sm font-medium">
                {contact.name || contact.profile_name || "Sem nome"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="text-muted-foreground h-4 w-4" />
              <span className="font-mono text-sm">{contact.phone_number}</span>
            </div>
            {contact.is_blocked && (
              <Badge variant="destructive" className="text-xs">
                <Ban className="mr-1 h-3 w-3" />
                Bloqueado
              </Badge>
            )}
            <div className="flex items-center gap-2">
              <MessageSquare className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground text-sm">
                {conversations.length} conversa{conversations.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversation selector */}
      {conversations.length > 1 && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Conversa:</span>
          <Select value={activeConversationId} onValueChange={setSelectedConversationId}>
            <SelectTrigger className="w-[400px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {conversations.map(conv => (
                <SelectItem key={conv.id} value={conv.id}>
                  {conv.status} - {conv.message_count} msgs
                  {conv.last_message_at &&
                    ` - ${new Date(conv.last_message_at).toLocaleDateString("pt-BR")}`}
                  {conv.is_handoff_active && " (handoff)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {conversations.length === 0 && (
        <div className="text-muted-foreground py-12 text-center">
          Este contato não possui conversas
        </div>
      )}

      {/* Timeline */}
      {activeConversationId && (
        <div className="space-y-3">
          {loadingTimeline && (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          )}

          {!loadingTimeline && allEvents.length === 0 && (
            <div className="text-muted-foreground py-12 text-center">
              Nenhum evento nesta conversa
            </div>
          )}

          {allEvents.map(event => (
            <TimelineEventCard key={event.id} event={event} />
          ))}

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Carregar mais
              </Button>
            </div>
          )}
        </div>
      )}
    </AdminPageContainer>
  )
}
